import type {
  GDSTheme,
  GDSStitchesFormattedTheme,
  GDSTypographyStyle,
  GDSShadows,
  GDSShadow,
  StringTokens,
  NumberTokens,
  GDSColors,
} from '@leagueplatform/genesis-core';
import {
  toPx,
  toRem,
  getNegativeSpacing,
  getFontWeightNumberByName,
  getFontWeightTokenByNumber,
  getFallbackValues,
  isStitchesFormatted,
} from '@leagueplatform/genesis-core';
import { ClientTheme } from '../types';
import { getFocusShadows, getFocusShadowsFromFlatColors } from '../shadows';

type FlatColorTokens = Record<string, string>;

const setDeep = (obj: Record<string, any>, path: string[], value: any) => {
  let curr: Record<string, any> = obj;
  path.forEach((segment, idx) => {
    if (idx === path.length - 1) {
      curr[segment] = value;
      return;
    }
    curr[segment] = curr[segment] ?? {};
    curr = curr[segment];
  });
};

/**
 * Mapping of flat camelCase color key prefixes to their nested path segments.
 * Ordered longest-first so more specific prefixes match before shorter ones
 * (e.g., 'primaryBackgroundCritical' before 'primaryBackground').
 *
 * To support a new color token pattern, add a prefix entry here.
 * The suffix after the prefix is automatically split at PascalCase
 * boundaries and lowercased to form the remaining path segments.
 */
const COLOR_PREFIX_MAP: ReadonlyArray<{ prefix: string; path: string[] }> = [
  // surface
  {
    prefix: 'surfaceSkeletonShimmer',
    path: ['surface', 'skeleton', 'shimmer'],
  },
  { prefix: 'surfaceScreenHeader', path: ['surface', 'screenHeader'] },
  { prefix: 'surfaceBackground', path: ['surface', 'background'] },
  { prefix: 'surfaceCard', path: ['surface', 'card'] },
  // onSurface
  { prefix: 'onSurfaceText', path: ['onSurface', 'text'] },
  { prefix: 'onSurfaceBorder', path: ['onSurface', 'border'] },
  { prefix: 'onSurfaceIcon', path: ['onSurface', 'icon'] },
  // primary (longer prefixes first)
  {
    prefix: 'primaryBackgroundCritical',
    path: ['primary', 'background', 'critical'],
  },
  { prefix: 'primaryTextCritical', path: ['primary', 'text', 'critical'] },
  { prefix: 'primaryBackground', path: ['primary', 'background'] },
  { prefix: 'primaryText', path: ['primary', 'text'] },
  // secondary
  {
    prefix: 'secondaryBackgroundCritical',
    path: ['secondary', 'background', 'critical'],
  },
  { prefix: 'secondaryBackground', path: ['secondary', 'background'] },
  { prefix: 'secondaryBorder', path: ['secondary', 'border'] },
  { prefix: 'secondaryText', path: ['secondary', 'text'] },
  // tertiary
  { prefix: 'tertiaryBackground', path: ['tertiary', 'background'] },
  { prefix: 'tertiaryBorder', path: ['tertiary', 'border'] },
  { prefix: 'tertiaryText', path: ['tertiary', 'text'] },
  // interactive (longer prefixes first)
  {
    prefix: 'interactiveBorderCritical',
    path: ['interactive', 'border', 'critical'],
  },
  { prefix: 'interactiveBackground', path: ['interactive', 'background'] },
  { prefix: 'interactiveAction', path: ['interactive', 'action'] },
  { prefix: 'interactiveBorder', path: ['interactive', 'border'] },
  { prefix: 'interactiveIcon', path: ['interactive', 'icon'] },
  { prefix: 'interactiveFocus', path: ['interactive', 'focus'] },
  // input
  { prefix: 'inputBackground', path: ['input', 'background'] },
  { prefix: 'inputBorder', path: ['input', 'border'] },
  // statuses (longer prefixes first)
  { prefix: 'criticalBackground', path: ['critical', 'background'] },
  { prefix: 'criticalBorder', path: ['critical', 'border'] },
  { prefix: 'critical', path: ['critical'] },
  { prefix: 'successBackground', path: ['success', 'background'] },
  { prefix: 'successBorder', path: ['success', 'border'] },
  { prefix: 'success', path: ['success'] },
  { prefix: 'warningBackground', path: ['warning', 'background'] },
  { prefix: 'warningBorder', path: ['warning', 'border'] },
  { prefix: 'warning', path: ['warning'] },
  { prefix: 'highlightBackground', path: ['highlight', 'background'] },
  { prefix: 'highlightBorder', path: ['highlight', 'border'] },
  { prefix: 'highlight', path: ['highlight'] },
  // decorative
  { prefix: 'decorativeBrand', path: ['decorative', 'brand'] },
  // dataViz (longer prefixes first)
  { prefix: 'dataVizInterfaceTrack', path: ['dataViz', 'interface', 'track'] },
  { prefix: 'dataVizSingleColor', path: ['dataViz', 'singleColor'] },
  { prefix: 'dataVizStatus', path: ['dataViz', 'status'] },
  { prefix: 'dataVizCategorical', path: ['dataViz', 'categorical'] },
  // tag (group name is dynamic, handled by PascalCase splitting of suffix)
  { prefix: 'tag', path: ['tag'] },
];

/**
 * Splits a PascalCase suffix into lowercase path segments.
 * e.g., 'PrimaryBright' → ['primary', 'bright']
 *       'Default' → ['default']
 *       '' → []
 */
const splitPascalCase = (str: string): string[] => {
  if (!str) return [];
  return str
    .split(/(?=[A-Z])/)
    .filter(Boolean)
    .map((s) => s.toLowerCase());
};

const expandFlatColorsForCommons = (flat: FlatColorTokens) => {
  const nested: Record<string, any> = {};

  Object.entries(flat).forEach(([key, value]) => {
    const mapping = COLOR_PREFIX_MAP.find((m) => key.startsWith(m.prefix));
    if (!mapping) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(
          `[expandFlatColorsForCommons] Unmapped color token: "${key}". ` +
            'Add a prefix entry to COLOR_PREFIX_MAP if this token needs nested expansion.'
        );
      }
      return;
    }

    const suffix = key.slice(mapping.prefix.length);
    const suffixSegments = splitPascalCase(suffix);
    const fullPath = [...mapping.path, ...suffixSegments];
    setDeep(nested, fullPath, value);

    // Back-compat: alias surfaceSkeletonShimmer → onSurface.skeleton
    if (key === 'surfaceSkeletonShimmerBackground') {
      setDeep(nested, ['onSurface', 'skeleton', 'background'], value);
    } else if (key === 'surfaceSkeletonShimmerOverlay') {
      setDeep(nested, ['onSurface', 'skeleton', 'highlight'], value);
    }
  });

  // Back-compat alias used by some theme helper stories.
  if (nested?.decorative?.brand?.primary?.default) {
    setDeep(
      nested,
      ['decorative', 'brand', 'default'],
      nested.decorative.brand.primary.default
    );
  }

  return {
    ...flat,
    ...nested,
  };
};

const wordToNumber = {
  One: 1,
  Two: 2,
  Three: 3,
  Four: 4,
};

const getMappedTypographyKey = (typographyKey: string) => {
  let mappedTypographyKey = typographyKey;
  Object.entries(wordToNumber).forEach(
    ([numberWord, numberValue]: [string, number]) => {
      const regexp = new RegExp(numberWord);
      mappedTypographyKey = mappedTypographyKey.replace(
        regexp,
        String(numberValue)
      );
    }
  );
  return mappedTypographyKey;
};

const getRemFontSizes = (themeInput: GDSTheme): ClientTheme['fontSizes'] => {
  const fontSizes: StringTokens = {};
  if (themeInput?.rem?.fontSizes) {
    Object.entries(themeInput.rem.fontSizes).forEach(
      ([fontSize, value]: [string, number]) => {
        const mappedFontSizeKey = getMappedTypographyKey(fontSize);
        fontSizes[mappedFontSizeKey] = String(value);
      }
    );
  }

  return toRem(fontSizes);
};

const getFontSizes = (themeInput: GDSTheme): ClientTheme['fontSizes'] => {
  const fontSizes: NumberTokens = {};
  if (themeInput?.typography) {
    Object.entries(themeInput.typography).forEach(
      ([typographyKey, { fontSize }]: [string, GDSTypographyStyle]) => {
        const mappedTypographyKey = getMappedTypographyKey(typographyKey);
        fontSizes[mappedTypographyKey] = fontSize;
      }
    );
  }

  return fontSizes;
};

const defaultFontWeights = {
  bold: 700,
  semibold: 600,
  medium: 500,
  regular: 400,
};

const getRemLineHeights = (
  themeInput: GDSTheme
): ClientTheme['lineHeights'] => {
  const lineHeights: StringTokens = {};
  if (themeInput?.rem?.fontSizes) {
    Object.entries(themeInput.rem.lineHeights).forEach(
      ([lineHeight, value]: [string, number]) => {
        const mappedTypographyKey = getMappedTypographyKey(lineHeight);
        lineHeights[mappedTypographyKey] = String(value);
      }
    );
  }

  return toRem(lineHeights);
};

const getLineHeights = (themeInput: GDSTheme): ClientTheme['lineHeights'] => {
  const lineHeights: NumberTokens = {};
  if (themeInput?.typography) {
    Object.entries(themeInput.typography).forEach(
      ([typographyKey, { lineHeight }]: [string, GDSTypographyStyle]) => {
        const mappedLineHeightKey = getMappedTypographyKey(typographyKey);
        lineHeights[mappedLineHeightKey] = lineHeight;
      }
    );
  }

  return toPx(lineHeights);
};

const getShadows = (
  shadows: GDSShadows,
  colors: GDSColors
): ClientTheme['shadows'] => {
  const shadowTokens: StringTokens = {};
  Object.entries(shadows).forEach(
    ([shadowKey, { x, y, blur, spread, color }]: [string, GDSShadow]) => {
      shadowTokens[shadowKey] = `${x}px ${y}px ${blur}px ${spread}px ${color}`;
    }
  );
  const focusShadows = getFocusShadows(colors);
  return {
    ...shadowTokens,
    ...focusShadows,
  };
};

const getMappedFontWeight = ({ fontWeight }: GDSTypographyStyle) => {
  if (typeof fontWeight === 'string') {
    const fontWeightValue = getFontWeightNumberByName(fontWeight);
    return getFontWeightTokenByNumber(fontWeightValue);
  }
  return getFontWeightTokenByNumber(fontWeight);
};

const getMappedFontFamily = (
  { fontFamily }: GDSTypographyStyle,
  fonts: ClientTheme['fonts']
) => {
  let font = fontFamily;
  Object.entries(fonts).forEach(([token, value]) => {
    if (font === value) {
      font = token;
    }
  });

  return font;
};

const mapTypographyTokenValues = (
  token: string,
  tokenValue: GDSTypographyStyle,
  { fonts, lineHeights, fontSizes }: any
) => ({
  ...tokenValue,
  fontSize: fontSizes[token],
  lineHeight: lineHeights[token],
  fontWeight: getMappedFontWeight(tokenValue),
  fontFamily: getMappedFontFamily(tokenValue, fonts),
  textTransform: tokenValue.textCase,
});

const getTypography = (theme: GDSTheme, commonsTheme) => {
  const typography = {};
  Object.entries(theme.typography).forEach(([token, tokenValue]) => {
    const mappedToken = getMappedTypographyKey(token);
    typography[token] = mapTypographyTokenValues(
      mappedToken,
      tokenValue,
      commonsTheme
    );
  });

  return typography;
};

export interface GenesisCommonsThemeOptions {
  useRems: boolean;
}

/**
 * Resolve a `$token` reference within a token scale.
 * e.g., `$body` in fonts → value of `fonts.body`.
 */
const resolveRef = (
  scale: Record<string, any>,
  key: string,
  depth = 0
): any => {
  const value = scale[key];
  if (typeof value === 'string' && value.startsWith('$')) {
    if (depth > 3) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(`[resolveRef] Max ref depth exceeded for key: "${key}"`);
      }
      return value;
    }
    const refKey = value.slice(1);
    if (scale[refKey] === undefined) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(
          `[resolveRef] Broken token reference: "${value}" (key: "${key}")`
        );
      }
      return undefined;
    }
    return resolveRef(scale, refKey, depth + 1);
  }
  return value;
};

/**
 * Parse a unit string like "32px", "2rem", "-4px", or "0" to a number.
 */
const parseToNumber = (value: string): number => {
  if (value == null) return 0;
  if (value === '0') return 0;
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : 0;
};

const formatStitchesThemeForCommons = (
  theme: GDSStitchesFormattedTheme,
  { useRems }: GenesisCommonsThemeOptions
): ClientTheme => {
  const unitTokens = useRems ? theme.rem : theme.px;

  // Typography keys are those with fontSizes entries (headingOne, bodyOne, etc.)
  const typographyKeys = new Set(Object.keys(unitTokens.fontSizes));

  // -- fonts: only base keys (headings, body), no typography aliases --
  const fonts: Record<string, string> = {};
  Object.entries(theme.fonts).forEach(([key, value]) => {
    if (!typographyKeys.has(key)) {
      fonts[key] = value as string;
    }
  });

  // -- space: numeric values, no typography keys, includes negatives --
  const space: Record<string, number> = {};
  Object.entries(unitTokens.space).forEach(([key]) => {
    if (typographyKeys.has(key)) return;
    const resolved = resolveRef(unitTokens.space, key);
    space[key] = parseToNumber(resolved);
  });

  // -- sizes: numeric values, no typography keys --
  const sizes: Record<string, number> = {};
  Object.entries(unitTokens.sizes).forEach(([key]) => {
    if (typographyKeys.has(key)) return;
    const resolved = resolveRef(unitTokens.sizes, key);
    sizes[key] = parseToNumber(resolved);
  });

  // -- fontSizes: mapped keys, resolve refs; px → numbers, rem → strings --
  const fontSizes: Record<string, any> = {};
  Object.entries(unitTokens.fontSizes).forEach(([key]) => {
    const mappedKey = getMappedTypographyKey(key);
    const resolved = resolveRef(unitTokens.fontSizes, key);
    fontSizes[mappedKey] = useRems ? resolved : parseToNumber(resolved);
  });

  // -- lineHeights: mapped keys, resolve refs; always strings (px or rem) --
  const lineHeights: Record<string, string> = {};
  Object.entries(unitTokens.lineHeights).forEach(([key]) => {
    const mappedKey = getMappedTypographyKey(key);
    lineHeights[mappedKey] = resolveRef(unitTokens.lineHeights, key);
  });

  // -- fontWeights: use default named weights, not typography-keyed weights --
  const fontWeights = defaultFontWeights;

  // -- radii: numeric values --
  const radii: Record<string, number> = {};
  Object.entries(theme.radii).forEach(([key, value]) => {
    radii[key] = parseToNumber(value as string);
  });

  // -- typography: reconstruct full entries matching raw path output --
  const typography: Record<string, any> = {};
  typographyKeys.forEach((key) => {
    const mappedKey = getMappedTypographyKey(key);

    // Resolve font alias ($body) to actual stack, then map to token name
    const resolvedFont = resolveRef(theme.fonts, key);
    let fontFamily: string = resolvedFont;
    Object.entries(fonts).forEach(([token, value]) => {
      if (fontFamily === value) fontFamily = token;
    });

    const resolvedTextTransform = resolveRef(theme.textTransforms, key);
    // paragraphSpacing comes from px.space for this key (always absolute px)
    const resolvedParagraphSpacing = resolveRef(theme.px.space, key);

    // Resolve font weight ref (e.g., "$bold" → "bold") or number → token name
    const rawWeight = resolveRef(theme.fontWeights, key);
    const fontWeight =
      typeof rawWeight === 'number'
        ? getFontWeightTokenByNumber(rawWeight)
        : rawWeight;

    typography[key] = {
      fontFamily,
      fontWeight,
      lineHeight: lineHeights[mappedKey],
      fontSize: fontSizes[mappedKey],
      letterSpacing: parseFloat(resolveRef(theme.letterSpacings, key)) || 0,
      paragraphSpacing: resolvedParagraphSpacing
        ? parseToNumber(resolvedParagraphSpacing)
        : 0,
      textCase: resolvedTextTransform,
      textDecoration: resolveRef(theme.textDecorations, key),
      textTransform: resolvedTextTransform,
    };
  });

  return {
    colors: expandFlatColorsForCommons(
      theme.colors as unknown as FlatColorTokens
    ),
    fonts,
    space,
    sizes,
    fontSizes,
    lineHeights,
    fontWeights,
    radii,
    shadows: {
      ...theme.shadows,
      ...getFocusShadowsFromFlatColors(theme.colors),
    },
    typography,
  };
};

const formatGDSThemeForCommons = (
  theme: GDSTheme,
  { useRems }: GenesisCommonsThemeOptions
): ClientTheme => {
  const resolvedTheme = getFallbackValues(theme);

  const {
    colors,
    spacing,
    borderRadii,
    fonts,
    shadows,
    fontWeights: coreFontWeights,
  } = resolvedTheme;
  const space = {
    ...spacing,
    ...getNegativeSpacing(spacing as unknown as NumberTokens),
  };
  const fontSizes = useRems
    ? getRemFontSizes(resolvedTheme)
    : getFontSizes(resolvedTheme);
  const lineHeights = useRems
    ? getRemLineHeights(resolvedTheme)
    : getLineHeights(resolvedTheme);
  const fontWeights = coreFontWeights ?? defaultFontWeights;
  const commonsThemeBase = {
    colors,
    fonts,
    space,
    fontSizes,
    lineHeights,
    fontWeights,
  };

  return {
    ...commonsThemeBase,
    sizes: spacing,
    radii: borderRadii,
    shadows: getShadows(shadows, colors),
    typography: getTypography(theme, commonsThemeBase),
  };
};

export const formatThemeForCommons = (
  theme: GDSTheme | GDSStitchesFormattedTheme,
  { useRems }: GenesisCommonsThemeOptions = {
    useRems: false,
  }
): ClientTheme => {
  if (isStitchesFormatted(theme)) {
    return formatStitchesThemeForCommons(theme, { useRems });
  }
  return formatGDSThemeForCommons(theme, { useRems });
};
